import { Check } from "lucide-react";

interface ProgressIndicatorProps {
  currentStep: number;
  steps: string[];
}

export function ProgressIndicator({
  currentStep,
  steps,
}: ProgressIndicatorProps) {
  return (
    <div className="mb-8 rounded-2xl border border-border bg-card px-4 py-4 shadow-sm sm:px-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Service request
          </p>
          <p className="text-sm font-medium text-foreground">
            Step {Math.min(currentStep, steps.length)} of {steps.length}
          </p>
        </div>
        <p className="text-sm text-muted-foreground">
          {steps[Math.max(0, Math.min(currentStep, steps.length) - 1)]}
        </p>
      </div>

      <div className="flex items-center justify-between">
        {steps.map((label, index) => {
          const stepNumber = index + 1;
          const isCompleted = stepNumber < currentStep;
          const isCurrent = stepNumber === currentStep;

          return (
            <div
              key={label}
              className="flex items-center flex-1 last:flex-initial"
            >
              <div className="flex min-w-0 flex-col items-center gap-2">
                <div
                  className={`
                    relative flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold
                    transition-all duration-300 ease-out
                    ${
                      isCompleted
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : isCurrent
                          ? "bg-primary text-primary-foreground ring-4 ring-primary/15 shadow-sm"
                          : "bg-muted text-muted-foreground"
                    }
                  `}
                >
                  {isCompleted ? <Check className="w-5 h-5" /> : <span>{stepNumber}</span>}
                </div>
                <span
                  className={`
                    max-w-20 text-center text-[11px] font-medium leading-tight transition-colors duration-200 sm:max-w-none sm:text-xs
                    ${
                      isCurrent
                        ? "text-primary"
                        : isCompleted
                          ? "text-foreground"
                          : "text-muted-foreground"
                    }
                  `}
                >
                  {label}
                </span>
              </div>

              {index < steps.length - 1 && (
                <div className="mx-3 h-0.5 flex-1 overflow-hidden rounded-full bg-muted">
                  <div
                    className={`
                      h-full rounded-full transition-all duration-500 ease-out
                      ${isCompleted ? "w-full bg-primary" : "w-0 bg-primary"}
                    `}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
